import { useCallback, useState } from "react";
import { DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { TrashIcon } from "@heroicons/react/20/solid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "../../contexts/LanguageContext";
import { DrawerShell } from "../../blocks/drawer";
import {
  fetchIntegration,
  fetchIntegrationAudiences,
  fetchPasses,
  fetchWebhookEvents,
} from "../../api/queries";
import {
  connectIntegration,
  disconnectIntegration,
  updateIntegration,
} from "../../api/mutations";
import {
  buildCreatePayload,
  buildEditPayload,
  createInitialValues,
  hasCustomerSync,
  hasEditFormChanged,
  isCreateSubmitBlocked,
  isEditFormValid,
  providerByKey,
  seedEditState,
} from "./providers";
import { integrationErrorLines } from "./errors";
import CredentialFields from "./components/CredentialFields";
import WebhookSection from "./components/WebhookSection";
import { DeliveriesTable, DeliveryDetail } from "./components/WebhookDeliveries";
import { BookingkitSection, CustomerSyncSection, ShopifySection } from "./components/EditSections";
import DisconnectDialog from "./components/DisconnectDialog";
import { ErrorBox, ProviderLogo, outlineButtonClass } from "./components/ui";

export const DELIVERIES_POLL_MS = 10000;

// One right-side drawer for connecting a provider (target.mode "create") and
// configuring a connected one ("edit"). `target` is { mode, key, id? }; the
// parent keeps passing the last target while the drawer animates closed.
//
// onClose closes whatever the drawer shows (Cancel, the X, the overlay).
// onDone(target) is for work that finishes later (a save, a connect, a
// disconnect): the parent closes only if the drawer still shows that target,
// so a request finishing after the user moved on can't close another drawer.
//
// The overlay and sliding panel are the shared DrawerShell; the header,
// scrolling body and footer (DrawerFrame) are this drawer's own, because each
// state below (loading, form, delivery detail) brings its own form and footer.
export default function IntegrationDrawer({ open, target, storeId, onClose, onDone = onClose }) {
  const provider = target ? providerByKey(target.key) : null;

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      maxWidthClassName={target?.key === "webhook" ? "max-w-[640px]" : "max-w-[500px]"}
    >
      {provider &&
        (target.mode === "edit" ? (
          <EditContent
            key={`edit-${target.id}`}
            provider={provider}
            integrationId={target.id}
            storeId={storeId}
            open={open}
            onClose={onClose}
            onDone={onDone}
          />
        ) : (
          <CreateContent
            key={`create-${provider.key}`}
            provider={provider}
            storeId={storeId}
            onClose={onClose}
            onDone={onDone}
          />
        ))}
    </DrawerShell>
  );
}

function DrawerFrame({ provider, description, onClose, onSubmit, footer, children }) {
  const { t } = useLanguage();
  return (
    <form onSubmit={onSubmit} className="flex h-full flex-col bg-white shadow-xl">
      <div className="flex items-start justify-between gap-3 px-6 pt-7 pb-2">
        <span className="flex min-w-0 items-center gap-3">
          <ProviderLogo provider={provider} size={40} />
          <span className="flex min-w-0 flex-col gap-0.5">
            <DialogTitle as="h2" className="text-xl font-semibold text-gray-900">
              {provider.name}
            </DialogTitle>
          </span>
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 pt-2 pb-6">
        <div className="flex flex-col gap-[18px]">
          {description && <p className="text-sm leading-normal text-gray-700">{description}</p>}
          {children}
        </div>
      </div>
      {footer && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-6 py-4">
          {footer}
        </div>
      )}
    </form>
  );
}

function SubmitButton({ disabled, children }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="inline-flex min-h-11 items-center justify-center rounded-[10px] bg-indigo-600 px-[18px] text-sm font-semibold text-white hover:bg-indigo-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-indigo-300"
    >
      {children}
    </button>
  );
}

function CancelButton({ onClick }) {
  const { t } = useLanguage();
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${outlineButtonClass} min-h-11 rounded-[10px] px-4 text-sm`}
    >
      {t("common.cancel")}
    </button>
  );
}

function useFormValues(initial) {
  const [values, setValues] = useState(initial);
  const setValue = useCallback(
    (name, value) => setValues((prev) => ({ ...prev, [name]: value })),
    [],
  );
  return [values, setValue, setValues];
}

// --- Create -----------------------------------------------------------------

function CreateContent({ provider, storeId, onClose, onDone }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [values, setValue, setValues] = useFormValues(createInitialValues);
  const [errorLines, setErrorLines] = useState([]);

  const connect = useMutation({
    mutationFn: () =>
      connectIntegration({
        storeId,
        integrationData: buildCreatePayload(provider.key, values),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["integrations", storeId] });
      onDone({ mode: "create", key: provider.key });
    },
    onError: (error) =>
      setErrorLines(integrationErrorLines(error, t("integrations.errors.connectFailed"), t)),
  });

  const submit = () => {
    setErrorLines([]);
    connect.mutate();
  };

  return (
    <DrawerFrame
      provider={provider}
      description={t(`integrations.description.${provider.key}`)}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      footer={
        <span className="ml-auto flex gap-2">
          <CancelButton onClick={onClose} />
          <SubmitButton
            disabled={connect.isPending || isCreateSubmitBlocked(provider.key, values)}
          >
            {connect.isPending ? t("common.saving") : t("integrations.connect")}
          </SubmitButton>
        </span>
      }
    >
      <ErrorBox lines={errorLines} />
      <CredentialFields provider={provider} values={values} setValue={setValue} />
      {provider.key === "webhook" && (
        <WebhookSection values={values} setValue={setValue} setValues={setValues} />
      )}
      {provider.key === "bookingkit" && (
        <p className="rounded-[10px] bg-blue-50 p-3 text-sm text-blue-900">
          {t("integrations.bookingkit.createHint")}
        </p>
      )}
    </DrawerFrame>
  );
}

// --- Edit -------------------------------------------------------------------

function EditContent({ provider, integrationId, storeId, open, onClose, onDone }) {
  const { t } = useLanguage();
  const {
    data: integration,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["integration", storeId, integrationId],
    queryFn: () => fetchIntegration(storeId, integrationId),
    enabled: !!storeId && !!integrationId,
  });

  if (isLoading || (!integration && !error)) {
    return (
      <DrawerFrame provider={provider} onClose={onClose}>
        <div className="flex flex-col gap-4" aria-busy="true">
          <p className="sr-only">{t("common.loading")}</p>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-11 animate-pulse rounded-[10px] bg-gray-100" />
          ))}
        </div>
      </DrawerFrame>
    );
  }

  if (error) {
    return (
      <DrawerFrame
        provider={provider}
        onClose={onClose}
        footer={
          <span className="ml-auto">
            <CancelButton onClick={onClose} />
          </span>
        }
      >
        <ErrorBox lines={integrationErrorLines(error, t("integrations.errors.loadFailed"), t)} />
      </DrawerFrame>
    );
  }

  return (
    <EditForm
      provider={provider}
      integration={integration}
      storeId={storeId}
      open={open}
      onClose={onClose}
      onDone={onDone}
    />
  );
}

function EditForm({ provider, integration, storeId, open, onClose, onDone }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const key = provider.key;
  const id = integration.id;

  const [seed] = useState(() => seedEditState(integration));
  const [values, setValue, setValues] = useFormValues(seed.values);
  const [errorLines, setErrorLines] = useState([]);
  const [deliveryId, setDeliveryId] = useState(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectErrors, setDisconnectErrors] = useState([]);

  const {
    data: audiences,
    isLoading: isAudiencesLoading,
    error: audiencesError,
  } = useQuery({
    queryKey: ["audiences", storeId, id],
    queryFn: () => fetchIntegrationAudiences(storeId, id, { silent: true }),
    enabled: !!storeId && hasCustomerSync(key),
  });

  const usesPasses = key === "shopify" || key === "bookingkit";
  const { data: passes = [], isLoading: isPassesLoading } = useQuery({
    queryKey: ["passes", storeId],
    queryFn: () => fetchPasses(storeId),
    enabled: !!storeId && usesPasses,
  });

  // Polls only while this drawer is open on a webhook.
  const isWebhook = key === "webhook";
  const {
    data: deliveries,
    isLoading: isDeliveriesLoading,
    error: deliveriesError,
  } = useQuery({
    queryKey: ["webhookEvents", storeId, id],
    queryFn: () => fetchWebhookEvents(storeId, id, { silent: true }),
    enabled: !!storeId && isWebhook && open,
    refetchInterval: isWebhook && open ? DELIVERIES_POLL_MS : false,
  });
  const deliveryList = Array.isArray(deliveries) ? deliveries : [];
  const [openedDelivery, setOpenedDelivery] = useState(null);
  const selectedDelivery =
    deliveryId !== null
      ? deliveryList.find((d) => d.id === deliveryId) || openedDelivery
      : null;

  const save = useMutation({
    mutationFn: () =>
      updateIntegration({
        storeId,
        integrationId: id,
        integrationData: buildEditPayload(key, values, integration),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["integrations", storeId] }),
        queryClient.invalidateQueries({ queryKey: ["integration", storeId, id] }),
      ]);
      onDone({ mode: "edit", key, id });
    },
    onError: (error) =>
      setErrorLines(integrationErrorLines(error, t("integrations.errors.updateFailed"), t)),
  });

  const disconnect = useMutation({
    mutationFn: () => disconnectIntegration({ storeId, integrationId: id }),
    onSuccess: async () => {
      // Close first: while the drawer is mounted its ["integration", …] and
      // webhook-events queries are active, and removing or invalidating them
      // refetches a record that no longer exists (a 404 toast). Once closed
      // they are inactive and gcTime 0 drops them.
      setDisconnectOpen(false);
      onDone({ mode: "edit", key, id });
      await queryClient.invalidateQueries({ queryKey: ["integrations", storeId] });
    },
    onError: (error) =>
      setDisconnectErrors(
        integrationErrorLines(
          error,
          t("integrations.errors.disconnectFailed", { name: provider.name }),
          t,
        ),
      ),
  });

  const canSave =
    !save.isPending &&
    hasEditFormChanged(key, values, seed.original) &&
    isEditFormValid(key, values);

  if (selectedDelivery) {
    return (
      <DrawerFrame provider={provider} onClose={onClose}>
        <DeliveryDetail
          delivery={selectedDelivery}
          onBack={() => {
            setDeliveryId(null);
            setOpenedDelivery(null);
          }}
        />
      </DrawerFrame>
    );
  }

  return (
    <>
      <DrawerFrame
        provider={provider}
        description={t("integrations.form.updateDescription", { integrationName: provider.name })}
        onClose={onClose}
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSave) return;
          setErrorLines([]);
          save.mutate();
        }}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setDisconnectErrors([]);
                setDisconnectOpen(true);
              }}
              className="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3.5 text-sm font-semibold text-red-700 hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700"
            >
              <TrashIcon className="h-4 w-4" aria-hidden="true" />
              {t("integrations.disconnect")}
            </button>
            <span className="ml-auto flex gap-2">
              <CancelButton onClick={onClose} />
              <SubmitButton disabled={!canSave}>
                {save.isPending ? t("common.updating") : t("common.save")}
              </SubmitButton>
            </span>
          </>
        }
      >
        <ErrorBox lines={errorLines} />
        <CredentialFields provider={provider} values={values} setValue={setValue} />
        {key === "shopify" && (
          <ShopifySection
            values={values}
            setValue={setValue}
            passes={passes}
            isPassesLoading={isPassesLoading}
          />
        )}
        {key === "bookingkit" && (
          <BookingkitSection
            values={values}
            setValue={setValue}
            passes={passes}
            isPassesLoading={isPassesLoading}
          />
        )}
        {hasCustomerSync(key) && (
          <CustomerSyncSection
            values={values}
            setValue={setValue}
            audiences={audiences}
            isAudiencesLoading={isAudiencesLoading}
            listName={integration.config?.listName}
            errorLines={
              audiencesError
                ? integrationErrorLines(
                    audiencesError,
                    t("integrations.errors.audiencesFailed", { name: provider.name }),
                    t,
                  )
                : null
            }
          />
        )}
        {isWebhook && (
          <>
            <WebhookSection values={values} setValue={setValue} setValues={setValues} />
            <DeliveriesTable
              deliveries={deliveryList}
              isLoading={isDeliveriesLoading}
              errorLines={
                deliveriesError
                  ? integrationErrorLines(
                      deliveriesError,
                      t("integrations.errors.deliveriesFailed"),
                      t,
                    )
                  : null
              }
              onView={(delivery) => {
                setOpenedDelivery(delivery);
                setDeliveryId(delivery.id);
              }}
            />
          </>
        )}
      </DrawerFrame>
      <DisconnectDialog
        open={disconnectOpen}
        integration={integration}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={() => {
          setDisconnectErrors([]);
          disconnect.mutate();
        }}
        isPending={disconnect.isPending}
        errorLines={disconnectErrors}
      />
    </>
  );
}
